import * as React from 'react';

import {
  Button,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import * as state from '../../_state/manageAdminAccessDialog.state';
import { useTranslations } from '../../i18n/useTranslations';

import { SelectOrganization } from '~/shared/components/select-fields/SelectOrganization';

export const ManageAdminAccessDialog = () => {
  const t = useTranslations();
  const toast = useToast();

  const [isOpen, setIsOpen] = useAtom(state.$manageAdminAccessDialogOpen);
  const formData = useAtomValue(state.$manageAdminAccessFormData);
  const isFormValid = useAtomValue(state.$manageAdminAccessFormValid);
  const isSaving = useAtomValue(state.$manageAdminAccessDialogSaving);

  const updateFormData = useSetAtom(state.$updateManageAdminAccessFormData);
  const grantAdminAccess = useSetAtom(state.$grantAdminAccessAction);
  const revokeAdminAccess = useSetAtom(state.$revokeAdminAccessAction);
  const closeDialog = useSetAtom(state.$closeManageAdminAccessDialog);

  const handleGrantAccess = React.useCallback(async () => {
    try {
      await grantAdminAccess();
      toast.success(t.manageAdminAccessDialog.grantSuccess);
    } catch (_error) {
      toast.error(t.manageAdminAccessDialog.grantError);
    }
  }, [grantAdminAccess, toast, t]);

  const handleRevokeAccess = React.useCallback(async () => {
    try {
      await revokeAdminAccess();
      toast.success(t.manageAdminAccessDialog.revokeSuccess);
    } catch (_error) {
      toast.error(t.manageAdminAccessDialog.revokeError);
    }
  }, [revokeAdminAccess, toast, t]);

  const handleOrganizationChange = React.useCallback(
    (organizationId: string | undefined) => {
      updateFormData({ selectedOrganizationId: organizationId ?? '' });
    },
    [updateFormData],
  );

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={() => setIsOpen(false)}
      className="@ManageAdminAccessDialog"
    >
      <DialogContent className="min-w-[500px]">
        <DialogHeader slot="title">
          <DialogTitle>{t.manageAdminAccessDialog.title}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-6 p-4">
          {/* User info display */}
          <div className="rounded-md bg-neutral/5 p-3">
            <div className="text-sm text-neutral/60">
              {t.manageAdminAccessDialog.userInfo}
            </div>
            <div className="font-medium text-neutral/90">
              {formData.userName}
            </div>
          </div>

          {/* Organization Selection */}
          <div>
            <div className="mb-3 font-medium text-neutral/70">
              {t.manageAdminAccessDialog.selectOrganization}
            </div>
            <SelectOrganization
              value={formData.selectedOrganizationId}
              onChange={handleOrganizationChange}
              placeholder={
                t.manageAdminAccessDialog.fields.organization.placeholder
              }
            />
          </div>

          {/* Instructions */}
          <div className="rounded-md border border-primary/20 bg-primary/5 p-3">
            <div className="text-sm text-primary/80">
              {t.manageAdminAccessDialog.instructions}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            aria-label={t.manageAdminAccessDialog.actions.cancel}
            variant="outline"
            color="neutral"
            onPress={closeDialog}
            isDisabled={isSaving}
          >
            {t.manageAdminAccessDialog.actions.cancel}
          </Button>
          <Button
            aria-label={t.manageAdminAccessDialog.actions.revoke}
            variant="outline"
            color="failure"
            onPress={handleRevokeAccess}
            isDisabled={!isFormValid || isSaving}
            isLoading={isSaving}
          >
            {t.manageAdminAccessDialog.actions.revoke}
          </Button>
          <Button
            aria-label={t.manageAdminAccessDialog.actions.grant}
            variant="solid"
            color="primary"
            onPress={handleGrantAccess}
            isDisabled={!isFormValid || isSaving}
            isLoading={isSaving}
          >
            {t.manageAdminAccessDialog.actions.grant}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
